import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { leaveApi } from '../../api/leave';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable } from '../../components/DataTable';
import { ReturnAction } from '../../components/ReturnAction';
import { ReturnedBanner } from '../../components/ReturnedBanner';
import { useToast } from '../../components/Toast';

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', submitted: 'Submitted', under_review: 'Under Review', returned: 'Returned for Redraft', approved: 'Approved', rejected: 'Rejected', cancelled: 'Cancelled',
};

/** Includes a Supervisor Review section, shown to everyone but only populated for those with direct reports awaiting decision - a person-specific check, not a role-based one. */
export function MyLeavePage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const typesQuery = useQuery({ queryKey: ['leave-types'], queryFn: () => leaveApi.types() });
  const balancesQuery = useQuery({ queryKey: ['my-leave-balances'], queryFn: () => leaveApi.myBalances() });
  const requestsQuery = useQuery({ queryKey: ['my-leave-requests'], queryFn: () => leaveApi.myRequests() });
  const pendingReviewQuery = useQuery({ queryKey: ['leave-pending-supervisor'], queryFn: () => leaveApi.pendingForSupervisor() });

  const { register, handleSubmit, reset } = useForm<{ leaveTypeId: string; startDate: string; endDate: string; reason?: string }>();

  const applyMutation = useMutation({
    mutationFn: (values: { leaveTypeId: string; startDate: string; endDate: string; reason?: string }) =>
      leaveApi.apply({ leaveTypeId: Number(values.leaveTypeId), startDate: values.startDate, endDate: values.endDate, reason: values.reason }),
    onSuccess: (result) => { showSuccess(`Leave request submitted (${result.data.daysRequested} day(s)).`); queryClient.invalidateQueries({ queryKey: ['my-leave-requests'] }); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to submit leave request.'),
  });

  const decideMutation = useMutation({
    mutationFn: ({ id, decision }: { id: number; decision: 'approved' | 'rejected' }) => leaveApi.supervisorDecide(id, { decision }),
    onSuccess: () => { showSuccess('Decision recorded.'); queryClient.invalidateQueries({ queryKey: ['leave-pending-supervisor'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to record decision.'),
  });

  return (
    <div className="page">
      <Link to="/staff">&larr; Back to Dashboard</Link>
      <h1>My Leave</h1>

      <section className="card">
        <h2>Leave Balances ({new Date().getFullYear()})</h2>
        {balancesQuery.data && (
          <DataTable
            rows={balancesQuery.data.data}
            rowKey={(b) => b.id}
            emptyMessage="No leave balances set yet - contact HR."
            columns={[
              { header: 'Type', render: (b) => b.leave_type_name },
              { header: 'Entitled', render: (b) => b.entitled_days },
              { header: 'Used', render: (b) => b.used_days },
              { header: 'Remaining', render: (b) => (Number(b.entitled_days) - Number(b.used_days)).toFixed(1) },
            ]}
          />
        )}
      </section>

      <section className="card">
        <h2>Apply for Leave</h2>
        <form className="inline-form" onSubmit={handleSubmit((values) => applyMutation.mutate(values))}>
          <label>
            Type
            <select {...register('leaveTypeId', { required: true })}>
              <option value="">Select…</option>
              {typesQuery.data?.data.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>
          <label>Start Date<input type="date" {...register('startDate', { required: true })} /></label>
          <label>End Date<input type="date" {...register('endDate', { required: true })} /></label>
          <label>Reason<input {...register('reason')} /></label>
          <button type="submit" disabled={applyMutation.isPending}>{applyMutation.isPending ? 'Submitting…' : 'Apply'}</button>
        </form>
      </section>

      <section className="card">
        <h2>My Requests</h2>
        {requestsQuery.data && (
          <DataTable
            rows={requestsQuery.data.data}
            rowKey={(r) => r.id}
            emptyMessage="No leave requests yet."
            columns={[
              { header: 'Type', render: (r) => r.leave_type_name },
              { header: 'Dates', render: (r) => `${r.start_date} - ${r.end_date}` },
              { header: 'Days', render: (r) => r.days_requested },
              { header: 'Status', render: (r) => <StatusBadge status={r.status} label={STATUS_LABELS[r.status]} /> },
            ]}
          />
        )}
        {requestsQuery.data?.data.filter((r) => r.status === 'returned').map((r) => (
          <ReturnedBanner key={r.id} returnComment={r.return_comment} resubmit={() => leaveApi.resubmit(r.id)} queryKeysToInvalidate={[['my-leave-requests']]} />
        ))}
      </section>

      {pendingReviewQuery.data && pendingReviewQuery.data.data.length > 0 && (
        <section className="card">
          <h2>Awaiting Your Review (Direct Reports)</h2>
          <DataTable
            rows={pendingReviewQuery.data.data}
            rowKey={(r) => r.id}
            emptyMessage=""
            columns={[
              { header: 'Employee', render: (r) => r.employee_name },
              { header: 'Type', render: (r) => r.leave_type_name },
              { header: 'Dates', render: (r) => `${r.start_date} - ${r.end_date}` },
              { header: 'Days', render: (r) => r.days_requested },
              {
                header: '',
                render: (r) => (
                  <span className="row-actions">
                    <button className="link-button" onClick={() => decideMutation.mutate({ id: r.id, decision: 'approved' })}>Approve</button>
                    <button className="link-button" onClick={() => decideMutation.mutate({ id: r.id, decision: 'rejected' })}>Reject</button>
                    <ReturnAction onReturn={(comment) => leaveApi.supervisorReturn(r.id, comment)} queryKeysToInvalidate={[['leave-pending-supervisor']]} />
                  </span>
                ),
              },
            ]}
          />
        </section>
      )}
    </div>
  );
}
